import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { useLocation } from 'react-router-dom';
import { formatReadableAmount } from '../../../../Common/FormatFunctions/FormatAmount';
import { RingLoader } from 'react-spinners';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, TableFooter } from "@mui/material";
import { FormaDateBalanceSheet } from '../../../../Common/FormatFunctions/FormatDate';
import PdfPreview from '../../../../Common/PdfPreview_Default';

const apikey = process.env.REACT_APP_API;

const DueDaysReport = () => {
    const location = useLocation();
    const Company_Name = sessionStorage.getItem('Company_Name')
    const Company_GSTNO = sessionStorage.getItem('Company_GSTNO')
    const searchParams = new URLSearchParams(location.search);
    const fromDate = searchParams.get('fromDate');
    const toDate = searchParams.get('toDate');
    const company_code = searchParams.get('company_code');
    const year_code = searchParams.get('year_code');
    const acCode = searchParams.get('acCode');
    const Party_Code = searchParams.get('Party_Code');
    const [pdfPreview, setPdfPreview] = useState(null);

    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [reportType, setReportType] = useState("PartyWise");

    const reportTypeFromUrl = searchParams.get("Report_Type") || "PartyWise";
    const reportTypeSaudaUrl = searchParams.get("Sauda_Type") || "ALL";

    useEffect(() => {
        setReportType(reportTypeFromUrl);
    }, [reportTypeFromUrl]);


    const [grandTotals, setGrandTotals] = useState({
        Bill_Amount: 0,
        Commission: 0,
        TDS_Amt: 0,
        Payable: 0,
    });

    const API_URL = `${apikey}/GetCustomerDueDaysListData`;

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear());
        return `${day}/${month}/${year}`;
    };



    useEffect(() => {
        const fetchReportData = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await axios.get(API_URL, {
                    params: {
                        Company_Code: company_code,
                        Year_Code: year_code,
                        Ac_Code: acCode,
                    },
                });
                setReportData(response.data.all_data);
            } catch (error) {
                console.error('Error fetching report:', error);
                setError('Error fetching report');
            } finally {
                setLoading(false);
            }
        };

        fetchReportData();
    }, [API_URL]);


    useEffect(() => {
        if (reportData.length > 0) {
            const totals = reportData.reduce(
                (acc, item) => {
                    const billAmt = Number(item.Bill_Amount) || 0;
                    const tds = Number(item.TDS_Amt) || 0;
                    const payable = billAmt - tds;

                    acc.Bill_Amount += billAmt;
                    acc.Commission += Number(item.Commission) || 0;
                    acc.TDS_Amt += tds;
                    acc.Payable += payable;

                    return acc;
                },
                { Bill_Amount: 0, Commission: 0, TDS_Amt: 0, Payable: 0 }
            );

            setGrandTotals(totals);
        }
    }, [reportData]);




    const handleExportToExcel = () => {
        const wb = XLSX.utils.book_new();
        let excelData = [];

        excelData.push([Company_Name]);
        excelData.push([`GSTN: ${Company_GSTNO}`]);
        excelData.push(["Sale Register"]);
        excelData.push([`From: ${fromDate} To: ${toDate}`]);
        excelData.push([]);

        let grandBill = 0, grandReceived = 0, grandBalance = 0, grandPay = 0;

        if (reportType === "PartyWise") {

            excelData.push([
                "Bill No", "Bill Date", "Party Name",
                "Bill Amount", "Commission", "TDS", "Payable"
            ]);

            const groupedData = groupByCustomerGlobal(reportData);

            Object.entries(groupedData).forEach(([_, group]) => {

                excelData.push([`Party : ${group.PartyName}`]);

                let partyBill = 0, partyReceived = 0, partyBalance = 0;

                group.rows.forEach(item => {
                    const bill = Number(item.Bill_Amount) || 0;
                    const comm = Number(item.Commission) || 0;
                    const tds = Number(item.TDS_Amt) || 0;
                    const pay = bill - tds;

                    const received = Number(item.Paid_Amount) || 0;
                    const balance = Number(item.Balance) || 0;

                    partyBill += bill;
                    partyReceived += received;
                    partyBalance += balance;

                    grandBill += bill;
                    grandReceived += received;
                    grandBalance += balance;

                    excelData.push([
                        item.doc_no,
                        item.Doc_date,
                        item.SaudaDate,
                        item.PartyName,
                        bill,
                        comm,
                        tds,
                        pay
                    ]);
                });

                excelData.push(["", "", "", "Party Total", partyBill, partyReceived, partyBalance]);
                excelData.push([]);
            });

        } else {


            excelData.push([
                "Party Name", "Bill Amount", "Commission", "TDS", "Payable"
            ]);

            reportData.forEach(item => {
                const bill = Number(item.Bill_Amount) || 0;
                const comm = Number(item.Commission) || 0;
                const tds = Number(item.TDS_Amt) || 0;
                const pay = bill - tds;

                const received = Number(item.Paid_Amount) || 0;
                const balance = Number(item.Balance) || 0;

                grandBill += bill;
                grandReceived += received;
                grandBalance += balance;

                excelData.push([
                    item.PartyName,
                    bill,
                    comm,
                    tds,
                    pay
                ]);
            });
        }

        excelData.push(["", "", "", "Grand Total", grandBill, grandReceived, grandBalance]);

        const ws = XLSX.utils.aoa_to_sheet(excelData);
        XLSX.utils.book_append_sheet(wb, ws, "SaleRegister");
        XLSX.writeFile(wb, "SaleRegister.xlsx");
    };


    const handlePrint = () => {
        const companyName = Company_Name;
        const fromDateParam = searchParams.get('fromDate');
        const toDateParam = searchParams.get('toDate');

        const doc = generatePDFDocument(companyName, fromDateParam, toDateParam);
        const pdfUrl = URL.createObjectURL(doc);
        const printWindow = window.open(pdfUrl);
        printWindow.onload = function () {
            setTimeout(() => {
                printWindow.print();
            }, 500);
        };
    };



    const generatePDFDocument = (companyName, fromDate, toDate) => {
        const doc = new jsPDF();

        // HEADER
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(companyName, doc.internal.pageSize.width / 2, 15, { align: 'center' });

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`GSTN : ${Company_GSTNO}`, doc.internal.pageSize.width / 2, 23, { align: 'center' });

        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text("Sale Register", doc.internal.pageSize.width / 2, 32, { align: 'center' });

        doc.setFontSize(10);
        // doc.text(`${FormaDateBalanceSheet(fromDate)} to ${FormaDateBalanceSheet(toDate)}`, doc.internal.pageSize.width / 2, 40, { align: 'center' });

        const bodyRows = [];

        let grandBill = 0, grandReceived = 0, grandBalance = 0, grandPay = 0;


        if (reportType === "PartyWise") {

            const groupedData = groupByCustomerGlobal(reportData);

            Object.entries(groupedData).forEach(([_, group]) => {

                let partyBill = 0, partyReceived = 0, partyBalance = 0;


                bodyRows.push([{
                    content: `Party : ${group.PartyName}`,
                    colSpan: 8,
                    styles: { fontStyle: 'bold', fillColor: [227, 242, 253] }
                }]);

                group.rows.forEach(row => {
                    const bill = Number(row.Bill_Amount) || 0;
                    const comm = Number(row.Commission) || 0;
                    const tds = Number(row.TDS_Amt) || 0;
                    const pay = bill - tds;

                    const received = Number(row.Paid_Amount) || 0;
                    const balance = Number(row.Balance) || 0;

                    partyBill += bill;
                    partyReceived += received;
                    partyBalance += balance;

                    grandBill += bill;
                    grandReceived += received;
                    grandBalance += balance;

                    bodyRows.push([
                        row.doc_no,
                        row.Doc_date,
                        row.SaudaDate || "-",
                        row.PartyName,
                        formatReadableAmount(bill),
                        formatReadableAmount(comm),
                        formatReadableAmount(tds),
                        formatReadableAmount(pay)
                    ]);
                });

                // Party Total
                bodyRows.push([
                    '', '', '',
                    { content: 'Party Total', styles: { fontStyle: 'bold' } },
                    formatReadableAmount(partyBill),
                    formatReadableAmount(partyReceived),
                    formatReadableAmount(partyBalance),
                ]);
            });

        } else {
            // TOTAL MODE (NON PARTYWISE)

            reportData.forEach(row => {
                const bill = Number(row.Bill_Amount) || 0;
                const comm = Number(row.Commission) || 0;
                const tds = Number(row.TDS_Amt) || 0;
                const pay = bill - tds;

                const received = Number(row.Paid_Amount) || 0;
                const balance = Number(row.Balance) || 0;

                grandBill += bill;
                grandReceived += received;
                grandBalance += balance;

                bodyRows.push([
                    row.PartyName,
                    formatReadableAmount(bill),
                    formatReadableAmount(comm),
                    formatReadableAmount(tds),
                    formatReadableAmount(pay)
                ]);
            });
        }

        // GRAND TOTAL
        bodyRows.push([
            '', '', '',
            { content: 'Grand Total', styles: { fontStyle: 'bold' } },
            formatReadableAmount(grandBill),
            formatReadableAmount(grandReceived),
            formatReadableAmount(grandBalance),
            formatReadableAmount(grandPay)
        ]);

        // TABLE HEAD BASED ON TYPE
        const head = reportType === "PartyWise"
            ? [["Bill No", "Bill Date",  "Party Name", "Bill Amount", "Commission", "TDS", "Payable"]]
            : [["Party Name", "Bill Amount", "Commission", "TDS", "Payable"]];

        doc.autoTable({
            head: head,
            body: bodyRows,
            startY: 45,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [255, 0, 0], textColor: [255, 255, 255] }
        });

        const pdfBlob = doc.output('blob');
        setPdfPreview(URL.createObjectURL(pdfBlob));

        return pdfBlob;
    };

    const groupReportData = (data) => {
        const groupedData = {};
        data.forEach((item) => {
            const key = `${item.salebillId}`;
            if (!groupedData[key]) {
                groupedData[key] = {
                    items: [],
                    totalQty: 0,
                };
            }
            groupedData[key].items.push(item);
            groupedData[key].totalQty += parseFloat(item.Bill_Amount) || 0;
        });
        return groupedData;
    };

    const groupedReportData = groupReportData(reportData);

    const groupByCustomer = (data) => {
        const grouped = {};

        data.forEach((item) => {
            const key = item.Ac_Code;

            if (!grouped[key]) {
                grouped[key] = {
                    PartyName: item.PartyName,
                    Bill_Amount: 0,
                    Commission: 0,
                    TDS_Amt: 0,
                    Payable: 0,
                    rows: []
                };
            }

            const billAmt = Number(item.Bill_Amount) || 0;
            const commission = Number(item.Commission) || 0;
            const tds = Number(item.TDS_Amt) || 0;
            const payable = billAmt - tds;


            grouped[key].Bill_Amount += billAmt;
            grouped[key].Commission += commission;
            grouped[key].TDS_Amt += tds;
            grouped[key].Payable += payable;

            grouped[key].rows.push(item);
        });

        return grouped;
    };

    const groupedData = groupByCustomer(reportData);


    const groupByDateGlobal = (data) => {
        const grouped = {};

        data.forEach(item => {
            const date = item.Doc_date;
            if (!grouped[date]) {
                grouped[date] = [];
            }
            grouped[date].push(item);
        });

        return grouped;
    };



    const totalColumns = reportType === "PartyWise" ? 2 : 1;


    const groupByCustomerGlobal = (data) => {
        const grouped = {};

        data.forEach(item => {
            const key = item.Party_Code;

            if (!grouped[key]) {
                grouped[key] = {
                    PartyName: item.PartyName,
                    rows: []
                };
            }

            grouped[key].rows.push(item);
        });

        return grouped;
    };



    return (
        <div style={{ marginTop: "10px" }}>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "24px", fontWeight: "bold" }}>{Company_Name}</Typography>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px", textDecoration: 'underline', fontWeight: "550" }}>GSTN : {Company_GSTNO}</Typography>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "20px", fontWeight: "bold" }}>Due Days Register</Typography>
            {/* <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px" }}>{FormaDateBalanceSheet(fromDate)} to {FormaDateBalanceSheet(toDate)}</Typography> */}

            <div className="mb-3 row align-items-center">
                <div className="col-auto">
                    <button className="btn btn-secondary me-2" onClick={handlePrint}>
                        Print
                    </button>
                    <button className="btn btn-success" onClick={handleExportToExcel}>
                        Export to Excel
                    </button>
                    <button className="btn btn-secondary me-2" onClick={() => generatePDFDocument(Company_Name, fromDate, toDate)}>
                        Preview PDF
                    </button>
                    {pdfPreview && (
                        <PdfPreview
                            pdfData={pdfPreview}
                            apiData={reportData[0]}
                            label={"SaleRegister"}
                        />
                    )}
                </div>
            </div>

            <div style={{
                maxHeight: 'calc(100vh - 200px)',
                overflow: 'auto',
                position: 'relative',
                border: '1px solid #e0e0e0',
                marginBottom: '60px'
            }} id="reportTable">

                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        backgroundColor: 'white'
                    }}>
                        <TableRow>
                            {reportType === "PartyWise" && (
                                <>
                                    <TableCell sx={{ fontWeight: "bold" }}>Bill No</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Bill Date</TableCell>
            
                                </>
                            )}

                            {/* <TableCell sx={{ fontWeight: "bold", textAlign: "left" }}>
                                Party Name
                            </TableCell> */}

                            <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>
                                Bill Amount
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>
                                Received
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>
                                Balance
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>
                                Due Days
                            </TableCell>
                        </TableRow>

                    </TableHead>

                    <TableBody>
                        {reportType === "PartyWise" ? (
                            Object.entries(groupByCustomerGlobal(reportData)).map(([custCode, group], dIdx) => {

                                let partyBill = 0, partyReceived = 0, partyBalance = 0;

                                return (
                                    <React.Fragment key={dIdx}>

                                        {/* 🔹 PARTY HEADER */}
                                        <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
                                            <TableCell colSpan={9} sx={{ fontWeight: "bold" }}>
                                                Party : {group.PartyName}
                                            </TableCell>
                                        </TableRow>

                                        {/* 🔹 ROWS */}
                                        {group.rows.map((row, i) => {
                                            const bill = Number(row.Bill_Amount) || 0;
                                            const received = Number(row.Paid_Amount) || 0;
                                            const balance = Number(row.Balance) || 0;

                                            const calculateDueDays = (docDateStr) => {
                                                const docDate = new Date(docDateStr);
                                                const today = new Date();

                                                if (isNaN(docDate)) return 0;

                                                const diffTime = today - docDate;
                                                return Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));
                                            };

                                            const dueDays = calculateDueDays(row.Doc_date);

                                            partyBill += bill;
                                            partyReceived += received;
                                            partyBalance += balance;

                                            return (
                                                <TableRow key={`${dIdx}-${i}`}>
                                                    <TableCell>{row.doc_no}</TableCell>
                                                    <TableCell align="center">{row.Doc_date}</TableCell>
                                                    {/* <TableCell>{row.PartyName}</TableCell> */}
                                                    <TableCell align="right">{formatReadableAmount(bill)}</TableCell>
                                                    <TableCell align="right">{formatReadableAmount(received)}</TableCell>
                                                    <TableCell align="right">{formatReadableAmount(balance)}</TableCell>
                                                    <TableCell align="right">{row.DueDays}</TableCell>
                                                </TableRow>
                                            );
                                        })}

                                        {/* PARTY TOTAL */}
                                        <TableRow sx={{ backgroundColor: "#fff9c4" }}>
                                            <TableCell colSpan={2} sx={{ fontWeight: "bold" }}>
                                                Party Total
                                            </TableCell>
                                            <TableCell align="right">{formatReadableAmount(partyBill)}</TableCell>
                                            <TableCell align="right">{formatReadableAmount(partyReceived)}</TableCell>
                                            <TableCell align="right">{formatReadableAmount(partyBalance)}</TableCell>
                                            {/* <TableCell align="right">{formatReadableAmount(partyPay)}</TableCell> */}
                                        </TableRow>

                                    </React.Fragment>
                                );
                            })
                        ) : (
                            /* Non PartyWise remains same */
                            reportData.map((row, idx) => {
                                const bill = Number(row.Bill_Amount) || 0;
                                const comm = Number(row.Commission) || 0;
                                const tds = Number(row.TDS_Amt) || 0;
                                const pay = bill - tds;

                                return (
                                    <TableRow key={idx}>
                                        <TableCell>{row.CustomerName}</TableCell>
                                        <TableCell align="right">{formatReadableAmount(bill)}</TableCell>
                                        <TableCell align="right">{formatReadableAmount(comm)}</TableCell>
                                        <TableCell align="right">{formatReadableAmount(tds)}</TableCell>
                                        <TableCell align="right">{formatReadableAmount(pay)}</TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                    <TableFooter
                        sx={{
                            position: 'sticky',
                            bottom: 0,
                            backgroundColor: 'white',
                            zIndex: 1
                        }}
                    >
                        <TableRow sx={{ backgroundColor: 'yellow' }}>
                            <TableCell
                                colSpan={totalColumns}
                                sx={{ fontWeight: 'bold', fontSize: '14px' }}
                            >
                                Total
                            </TableCell>


                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '14px' }}>{formatReadableAmount(grandTotals.Bill_Amount)}</TableCell>
                            <TableCell></TableCell>
                            {/* <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '14px' }}> {formatReadableAmount(grandTotals.Commission)} </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '14px' }}> {formatReadableAmount(grandTotals.TDS_Amt)} </TableCell> */}
                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '14px' }}> {formatReadableAmount(grandTotals.Payable)} </TableCell>
                        </TableRow>
                    </TableFooter>

                </Table>
            </div>

            {loading && (
                <div style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 9999
                }}>
                    <RingLoader size={80} />
                </div>
            )}
            {error && <div className="alert alert-danger">{error}</div>}
        </div>
    );
};

export default DueDaysReport;
